import React, { memo, type ReactElement, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box, TabContent } from '@saviynt/design-system';
import cx from 'classnames';

import { ENTITLEMENT_DETAILS_TABS } from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import { detailsActions } from '../../data-store/detailsSlice';

import { InherentRisksTab } from './InherentRisksTab/components';
import EntitlementSummary from './SummaryTab/EntitlementSummary';
import { UsersTab } from './UsersTab/components';
import EntitlementViolationsTab from './ViolationsTab/EntitlementViolationsTab';
import {
  RiskDetailsConflictsTab,
  RiskDetailsSummaryTab,
} from './InherentRiskDetailsTab';

import './EntitlementDetails.css';

interface EntitlementDetailsProps {
  activeTab: string;
  className?: string;
}

/**
 * EntitlementDetails
 */
function EntitlementDetails({
  activeTab,
  className,
}: EntitlementDetailsProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();
  const wrapperClasses = cx(
    'Sod-EntitlementDetails',
    activeTab.includes('violation') && 'Sod-EntitlementDetails-violationsTab',
    activeTab.includes('conflicts') && 'Sod-EntitlementDetails-conflictsTab',
    className
  );

  const {
    entitlementRisks: {
      sidepanel: { isSidePanelVisible: risksVisible },
    },
    entitlementUsers: {
      sidepanel: { isSidePanelVisible: usersVisible },
    },
  } = useTypedSelector((state) => state.aag.details);

  // Track activeTab changes and dispatch to store
  useEffect(() => {
    dispatch(detailsActions.setActiveTab(activeTab));
  }, [dispatch, activeTab]);

  // Close sidepanels when switching away from their respective tabs
  useEffect(() => {
    if (activeTab !== ENTITLEMENT_DETAILS_TABS.TAB_RISKS && risksVisible) {
      dispatch(
        detailsActions.setEntitlementRisksSidePanelRowContext({
          isSidePanelVisible: false,
          rowContext: { current: { riskId: 0 } },
        })
      );
    }

    if (activeTab !== ENTITLEMENT_DETAILS_TABS.TAB_USERS && usersVisible) {
      dispatch(
        detailsActions.setEntitlementUsersSidePanelRowContext({
          isSidePanelVisible: false,
          rowContext: { current: { userKey: null } },
        })
      );
    }
  }, [activeTab, risksVisible, usersVisible, dispatch]);

  const tabConfigs: {
    key: keyof typeof ENTITLEMENT_DETAILS_TABS;
    label: string;
    content?: ReactElement;
  }[] = [
    {
      key: 'TAB_SUMMARY',
      label: l('Summary'),
      content: <EntitlementSummary />,
    },
    {
      key: 'TAB_RISKS',
      label: l('Inherent Risks'),
      content: <InherentRisksTab />,
    },
    {
      key: 'TAB_USERS',
      label: l('Users'),
      content: <UsersTab />,
    },
    {
      key: 'TAB_VIOLATIONS',
      label: l('Violations'),
      content: <EntitlementViolationsTab />,
    },
    {
      key: 'TAB_RISKS_CONFLICTS',
      label: l('Conflicts'),
      content: <RiskDetailsConflictsTab />,
    },
    {
      key: 'TAB_RISKS_SUMMARY',
      label: l('Summary'),
      content: <RiskDetailsSummaryTab />,
    },
  ];

  const activeTabConfig = tabConfigs.find(
    ({ key }) => ENTITLEMENT_DETAILS_TABS[key] === activeTab
  );

  return (
    <Box tag='div' className={wrapperClasses}>
      {activeTabConfig && (
        <TabContent
          className={cx(
            `Sod-EntitlementDetails-tabContent-${activeTabConfig.key}`
          )}
          value={activeTab}
          tabValue={ENTITLEMENT_DETAILS_TABS[activeTabConfig.key]}>
          {activeTabConfig.content ?? (
            <>
              This is the {activeTabConfig.label} tab ({activeTab})
            </>
          )}
        </TabContent>
      )}
    </Box>
  );
}

EntitlementDetails.defaultProps = {
  className: null,
};

export default memo(EntitlementDetails);
